import React from 'react';
import { FlashMessageContext } from 'components/app-context/FlashMessageContextProvider';
import styled from 'styled-components';
import { useAfterTransition } from '../../hooks/useAfterTransition';
import FormAddedFlashMessage from 'components/docs-and-forms/true-form/FormAddedFlashMessage';

export function FlashMessages(props) {
  const { flashMessages } = React.useContext(FlashMessageContext);
  const unexpiredFlashMessages = React.useMemo(() => {
    return flashMessages.filter(v => {
      return (
        (v.lifetime && v.lifetime > new Date() - v.timestamp) || !v.lifetime
      );
    });
  }, [flashMessages]);

  return unexpiredFlashMessages?.length > 0 ? (
    <StyledFlashMessageContainer
      className={`${props.className || ''} flash-message-list`}
    >
      {unexpiredFlashMessages.map(msg => {
        return <FlashMessage msg={msg} key={msg.timestamp} />;
      })}
    </StyledFlashMessageContainer>
  ) : null;
}

const CustomMessages = { FormAddedFlashMessage: FormAddedFlashMessage };

function FlashMessage({ msg, ...props }) {
  const { deleteMessage } = React.useContext(FlashMessageContext);
  const [dismissed, setDismissed] = React.useState(false);
  const messageItemRef = React.useRef();

  const iconClassName = React.useMemo(() => {
    return msg?.icon === undefined ? 'fas fa-exclamation-circle' : msg.icon;
  }, [msg]);

  const handleDismissClicked = React.useCallback(() => {
    setDismissed(true);
  }, [setDismissed]);

  const afterTransition = React.useCallback(() => {
    dismissed && deleteMessage(msg);
  }, [dismissed, deleteMessage, msg]);

  useAfterTransition(messageItemRef, afterTransition);

  React.useEffect(() => {
    let timeout;
    const remainingTime =
      msg.timestamp.getTime() + msg.lifetime - new Date().getTime();

    if (remainingTime > 0) {
      timeout = setTimeout(() => {
        setDismissed(true);
      }, remainingTime + 10000);
    }
    return () => {
      if (msg.autoDismiss) deleteMessage(msg);
      return clearTimeout(timeout);
    };
  }, [msg]);

  const flashMessageComponent = React.useMemo(() => {
    const customComponent = msg.component && CustomMessages[msg.component];
    return customComponent ? (
      React.createElement(customComponent, msg.props)
    ) : (
      <span
        dangerouslySetInnerHTML={{
          __html: msg.msg,
        }}
      />
    );
  }, [msg]);

  return (
    <StyledFlashMessageItem
      className={`${props.className || ''} flash-message-item`}
      $dismissed={dismissed === true}
      ref={messageItemRef}
      data-type={msg.type || 'caution'}
    >
      {iconClassName && (
        <div className="flash-message-icon">
          <i className={iconClassName} />
        </div>
      )}
      <div>{flashMessageComponent}</div>
      <div>
        {msg.undoUrl && <button type="button">Undo</button>}
        {!msg.undismissable && (
          <button
            type="button"
            className="dismiss-button"
            onClick={handleDismissClicked}
          >
            {msg.dismissIcon ? <i className={msg.dismissIcon} /> : 'Dismiss'}
          </button>
        )}
      </div>
    </StyledFlashMessageItem>
  );
}

const StyledFlashMessageContainer = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const StyledFlashMessageItem = styled.li`
  align-items: center;
  background-color: #fff7e2;
  border-radius: 3px;
  border: solid 1px #fdba12;
  color: #ce8421;
  display: flex;
  font-family: 'Zilla Slab', serif;
  font-size: 16px;
  font-weight: normal;
  margin: 0;
  margin-bottom: 24px;
  opacity: ${({ $dismissed }) => ($dismissed ? 0 : 1)};
  padding: 6px 12px 8px 17px;
  transition: opacity 1.25s ease-in-out;

  > div {
    flex: 1 1 auto;
    &.flash-message-icon,
    &:last-child {
      flex: 0 1 auto;
    }
    &.flash-message-icon {
      i {
        color: #ce8421;
        font-size: 21px;
        margin-right: 8px;
        vertical-align: -6px;
      }
    }
    > button {
      font-weight: bold;
      font-family: 'Zilla Slab', serif;
      font-size: 16px;
      + button {
        margin-left: 1em;
      }
    }
  }
  &[data-type='success'] {
    color: #007a55;
    border: solid 1px #007a55;
    background-color: #f1fff6;
    > div {
      a,
      a:visited {
        color: inherit;
        text-decoration: underline;
      }
      &.flash-message-icon {
        i {
          color: #007a55;
        }
      }
      .dismiss-button {
        i.fa-times {
          font-size: 24px;
          padding: 4px 0;
        }
      }
    }
  }
`;
